/**
 * MiharuAI - LLMOps Monitoring Framework
 * 
 * Open-source framework for LLM operations monitoring that runs on TypeScript.
 * Intercepts LLM API calls and provides features such as cost measurement, 
 * tracing, and error tracking.
 */

import { MiharuInstanceManager } from '@/core/instance-manager'
import { validateConfig } from '@/utils/config'
import type { MiharuConfig } from '@/types/config'
import type { InstanceContext, MiharuInstance } from '@/types/core'

// Main API
export const miharu = {
  /**
   * Initialize a new Miharu instance with the provided configuration
   */
  async init(userConfig: Partial<MiharuConfig> = {}, context?: InstanceContext): Promise<MiharuInstance> {
    const config = validateConfig(userConfig)
    const instance = MiharuInstanceManager.create(config, context)
    await instance.init()
    return instance
  },

  /**
   * Get an existing instance by ID
   */
  getInstance(instanceId: string): MiharuInstance | undefined {
    return MiharuInstanceManager.getInstance(instanceId)
  },

  /**
   * Get all instances
   */
  getAllInstances(): MiharuInstance[] {
    return MiharuInstanceManager.getAllInstances()
  },

  /**
   * Get instances by context filter
   */
  getByContext(contextFilter: Partial<InstanceContext>): MiharuInstance[] {
    return MiharuInstanceManager.getByContext(contextFilter)
  },

  /**
   * Shutdown all instances
   */
  async shutdownAll(): Promise<void> {
    await MiharuInstanceManager.shutdownAll()
  },

  /**
   * Shutdown instances by context
   */
  async shutdownByContext(contextFilter: Partial<InstanceContext>): Promise<void> {
    await MiharuInstanceManager.shutdownByContext(contextFilter)
  },

  /**
   * Get statistics about all instances
   */
  getInstanceStats() {
    return MiharuInstanceManager.getInstanceStats()
  }
}

// Type exports for consumers
export type { MiharuConfig } from '@/types/config'
export type { InstanceContext, MiharuInstance } from '@/types/core'
export type { 
  TraceData, 
  LLMCallData, 
  ErrorData, 
  PatchResult, 
  InstanceStats, 
  InstanceMetrics 
} from '@/types/core'

// Error exports
export { MiharuError, SecurityError } from '@/types/core'
export { MiharuConfigError } from '@/types/config'

// Utility exports
export { generateEnvDocs } from '@/utils/config'

// Default export
export default miharu

// Version information
export const version = '0.1.0'

// Feature flags for Phase 1
export const features = {
  multiInstance: true,
  configValidation: true,
  environmentVariables: true,
  typeScript: true,
  // Phase 1-2 completed features
  providerPatching: true,
  openaiSupport: true,
  streamingSupport: true,
  sdkAnalysis: true,
  // Features coming in later phases
  dataStorage: false,
  performanceMonitoring: false,
  anthropicSupport: false,
  googleSupport: false
} as const